import { useState, useEffect, useRef, FormEvent, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CreditCard, Lock, XCircle, Loader2, Check, Monitor } from 'lucide-react';
import { Link } from 'react-router-dom';
import { login, rfidLogin, ApiException } from '../api/client';
import { sichereRuecksprungAdresse } from '../lib/weiterleitung';

// DEBUG: RFID-Logs (auf false fuer Production)
const RFID_DEBUG = false;
function rfidLog(msg: string, data?: Record<string, unknown>) {
  if (RFID_DEBUG) {
    console.info(`[rfid] ${msg}`, data || '');
  }
}

/**
 * Anmeldung — die eine Seite, die beide Welten bedient.
 *
 * Sie laeuft VOR der Anmeldung: kein angemeldeter Benutzer, keine Navigation.
 * Darum die ruhige, mittige Karte im Leitstand-Stil statt des vollen Rahmens.
 *
 * WICHTIG — die Masse hier sind Betriebsentscheidung, nicht Gestaltung:
 * Diese Seite steht auch an den Stationen in der Halle, wo im Stehen und
 * teils mit schlechten Augen gearbeitet wird. Farben und Bausteine kommen
 * vom Leitstand, die Masse bleiben grosszuegig (siehe docs/BETRIEB-ELIS.md,
 * "Stationen: Die Groessen sind Absicht"). Wer hier die dichten
 * Leitstand-Masse einsetzt, macht die Stationen unbrauchbar.
 *
 * SEC-006: Stationsabhaengige Darstellung
 * - Unregistrierte Station: Nur neutrales Admin-Login, keine Chip-Hinweise
 * - Registrierte Station: Chip und Admin nebeneinander, Chip voreingestellt
 */

/* Bedienelemente in Stations-Groesse — bewusst grosszuegiger als der Leitstand. */
const EINGABE =
  'w-full rounded-md border border-linie-2 bg-flaeche-2 px-3 py-2 text-tinte placeholder:text-tinte-3 focus:border-akzent focus:outline-none';
const KNOPF_PRIMAER =
  'w-full rounded-md border border-akzent bg-akzent px-4 py-2 font-medium text-akzent-satz transition-colors disabled:opacity-50';
const KNOPF_LEISE =
  'w-full rounded-md border border-linie-2 bg-flaeche px-4 py-2 text-tinte-2 transition-colors hover:text-tinte';

/** Die Wortmarke ueber der Karte — der einzige Anker ohne Navigation. */
function Wortmarke() {
  return (
    <p className="mb-5 text-center font-zahl text-[15px] font-semibold tracking-[0.06em] text-tinte-2">
      wash<b className="font-semibold text-akzent">·</b>ops
    </p>
  );
}

/** Aussenflaeche aller Anmelde-Seiten: dunkler Grund, Karte in der Mitte. */
function Anmeldeflaeche({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-grund text-tinte flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Wortmarke />
        <div className="rounded-leitstand border border-linie bg-flaeche p-8">{children}</div>
      </div>
    </div>
  );
}

/** Fehler stehen ueber dem Formular und sagen, was zu tun ist. */
function Fehlerfeld({ text }: { text: string }) {
  return (
    <div className="mb-4 rounded-md border border-stoer bg-flaeche-2 p-3 text-center text-stoer">
      {text}
    </div>
  );
}

type LoginMode = 'rfid' | 'password';
type RfidState = 'ready' | 'scanning' | 'success' | 'error';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // returnTo aus URL Query Parameter
  const returnTo = sichereRuecksprungAdresse(searchParams.get('returnTo'));

  // SEC-006: Station-Status
  const [stationStatus, setStationStatus] = useState<'loading' | 'registered' | 'unregistered'>(
    'loading',
  );

  // Login mode (nur relevant wenn registriert)
  const [mode, setMode] = useState<LoginMode>('password');

  // Password login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // RFID state
  const [rfidState, setRfidState] = useState<RfidState>('ready');
  const [chipBuffer, setChipBuffer] = useState('');
  const emailInputRef = useRef<HTMLInputElement>(null);
  const rfidInputRef = useRef<HTMLInputElement>(null);
  const scanTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const refocusTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Inaktivitäts-Timer Konstanten (Kiosk-Standard)
  const SCAN_COMPLETE_DELAY_MS = 200; // Nach 200ms ohne Eingabe gilt Scan als komplett
  const MIN_CHIP_LENGTH = 8;

  // Station-Check beim Laden (SEC-006)
  useEffect(() => {
    const checkStation = async () => {
      try {
        const res = await fetch('/api/station/status', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (data.id) {
            // Station ist gekoppelt
            setStationStatus('registered');
            setMode('rfid'); // RFID als Default auf registrierten Stationen
          } else {
            setStationStatus('unregistered');
            setMode('password');
          }
        } else if (res.status === 404) {
          // Keine Station = unregistriert
          setStationStatus('unregistered');
        } else {
          setStationStatus('unregistered');
        }
      } catch {
        setStationStatus('unregistered');
      }
    };
    checkStation();
  }, []);

  // Auto-Focus auf E-Mail-Feld bei Password-Mode
  useEffect(() => {
    if (mode === 'password' && stationStatus !== 'loading' && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [mode, stationStatus]);

  // RFID Login Handler
  const doRfidLogin = useCallback(
    async (chipId: string, trigger: 'ENTER' | 'TIMEOUT') => {
      rfidLog('doRfidLogin called', {
        chipId: chipId.substring(0, 4) + '...',
        length: chipId.length,
        trigger,
      });

      if (loading) {
        rfidLog('doRfidLogin aborted: already loading');
        return;
      }

      const normalized = chipId
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '');
      rfidLog('normalized chipId', {
        normalized: normalized.substring(0, 4) + '...',
        length: normalized.length,
      });

      if (normalized.length < MIN_CHIP_LENGTH) {
        rfidLog('doRfidLogin aborted: too short', {
          length: normalized.length,
          min: MIN_CHIP_LENGTH,
        });
        setChipBuffer('');
        setRfidState('ready');
        return;
      }

      setLoading(true);
      setRfidState('scanning');
      setError('');

      try {
        rfidLog('calling POST /api/station/rfid', { chipId: normalized.substring(0, 4) + '...' });
        const result = await rfidLogin(normalized);
        rfidLog('rfidLogin SUCCESS', {
          redirectUrl: result.redirectUrl,
          expiresIn: result.expiresIn,
        });

        // Erfolg: Kurz grün zeigen, dann redirect
        setRfidState('success');
        setChipBuffer('');
        setTimeout(() => {
          navigate(result.redirectUrl || '/station');
        }, 500);
      } catch (err) {
        rfidLog('rfidLogin ERROR', { err });
        if (err instanceof ApiException) {
          setError(err.data.error || 'Chip nicht erkannt');
          rfidLog('ApiException', { code: err.data.code, error: err.data.error });
        } else {
          setError('Keine Verbindung zum Server. Bitte den Chip noch einmal an den Leser halten.');
        }
        setRfidState('error');
        setChipBuffer('');
        // Nach 2.5s zurück zu ready
        setTimeout(() => {
          setRfidState('ready');
          setError('');
          // Refocus nach Error
          rfidInputRef.current?.focus();
        }, 2500);
      } finally {
        setLoading(false);
      }
    },
    [loading, navigate],
  );

  // Hidden Input Handler für RFID (robuster als globaler keypress)
  const handleRfidInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      rfidLog('onChange', {
        newValue,
        length: newValue.length,
        activeElement: document.activeElement?.tagName,
      });

      // Während success/error: Eingaben ignorieren
      if (rfidState === 'success' || rfidState === 'error') {
        rfidLog('onChange ignored: rfidState', { rfidState });
        return;
      }

      setChipBuffer(newValue);

      // Visuelles Feedback: Nach erstem Zeichen scanning
      if (newValue.length > 0 && rfidState === 'ready') {
        setRfidState('scanning');
      }

      // Inaktivitäts-Timer: Nach SCAN_COMPLETE_DELAY_MS ohne Eingabe gilt Scan als komplett
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }

      if (newValue.length >= MIN_CHIP_LENGTH) {
        scanTimeoutRef.current = setTimeout(() => {
          rfidLog('scan complete via TIMEOUT', { buffer: newValue });
          doRfidLogin(newValue, 'TIMEOUT');
        }, SCAN_COMPLETE_DELAY_MS);
      }
    },
    [rfidState, doRfidLogin],
  );

  const handleRfidKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      rfidLog('onKeyDown', { key: e.key, bufferLength: chipBuffer.length });

      if (e.key === 'Enter') {
        e.preventDefault();
        // Enter-Trigger: Sofort absenden
        if (scanTimeoutRef.current) {
          clearTimeout(scanTimeoutRef.current);
          scanTimeoutRef.current = null;
        }
        rfidLog('scan complete via ENTER', { buffer: chipBuffer });
        doRfidLogin(chipBuffer, 'ENTER');
      }
    },
    [chipBuffer, doRfidLogin],
  );

  // Fokus-Robustheit: Auto-Focus und Refocus bei Blur (Kiosk-Standard)
  useEffect(() => {
    if (mode !== 'rfid' || stationStatus !== 'registered') {
      return;
    }

    rfidLog('Chip-Tab mounted', { mode, stationStatus, rfidState });

    // Initial Focus
    const focusInput = () => {
      if (rfidInputRef.current && document.activeElement !== rfidInputRef.current) {
        rfidInputRef.current.focus();
        rfidLog('focus set on rfidInput');
      }
    };

    focusInput();

    // Refocus bei Blur (nach kurzer Verzögerung)
    const handleBlur = () => {
      if (rfidState === 'ready' || rfidState === 'scanning') {
        refocusTimeoutRef.current = setTimeout(focusInput, 100);
      }
    };

    const input = rfidInputRef.current;
    input?.addEventListener('blur', handleBlur);

    // Cleanup
    return () => {
      input?.removeEventListener('blur', handleBlur);
      if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current);
      if (refocusTimeoutRef.current) clearTimeout(refocusTimeoutRef.current);
    };
  }, [mode, stationStatus, rfidState]);

  // Klick auf Card fokussiert hidden input
  const handleCardClick = useCallback(() => {
    rfidLog('card clicked, focusing input');
    rfidInputRef.current?.focus();
  }, []);

  // Password Login Handler
  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.requiresTwoFactor) {
        // returnTo an 2FA-Seite weitergeben
        navigate(`/2fa/verify?returnTo=${encodeURIComponent(returnTo)}`);
      } else if (result.requiresTwoFactorSetup) {
        // returnTo an 2FA-Setup weitergeben
        navigate(`/2fa/setup?returnTo=${encodeURIComponent(returnTo)}`);
      } else {
        // Direkt zu returnTo navigieren
        navigate(returnTo);
      }
    } catch (err) {
      if (err instanceof ApiException) {
        if (err.data.code === 'RATE_LIMITED') {
          setError(
            `Zu viele Versuche. Bitte ${err.data.retryAfter} Sekunden warten, dann noch einmal anmelden.`,
          );
        } else {
          setError(
            err.data.error || 'E-Mail oder Passwort stimmt nicht. Bitte noch einmal eingeben.',
          );
        }
      } else {
        setError('Keine Verbindung zum Server. Bitte noch einmal versuchen.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Wird noch geprueft, ob dieses Geraet eine gekoppelte Station ist
  if (stationStatus === 'loading') {
    return (
      <Anmeldeflaeche>
        <div className="py-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-tinte-3" />
        </div>
      </Anmeldeflaeche>
    );
  }

  // UNREGISTRIERT: Nur neutrales Admin-Login (SEC-006 - keine RFID-Hinweise)
  if (stationStatus === 'unregistered') {
    return (
      <Anmeldeflaeche>
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-tinte">Anmeldung</h1>
        </div>

        {error && <Fehlerfeld text={error} />}

        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-tinte-2 mb-1">
              E-Mail
            </label>
            <input
              id="email"
              type="email"
              ref={emailInputRef}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={EINGABE}
              required
              autoComplete="email"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-tinte-2 mb-1">
              Passwort
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={EINGABE}
              required
              autoComplete="current-password"
            />
          </div>

          <button type="submit" disabled={loading || !email || !password} className={KNOPF_PRIMAER}>
            {loading ? 'Wird geprüft...' : 'Anmelden'}
          </button>

          <div className="text-center mt-3">
            <Link
              to="/forgot-password"
              className="text-sm text-tinte-3 transition-colors hover:text-tinte-2"
            >
              Passwort vergessen?
            </Link>
          </div>
        </form>

        {/* Station koppeln */}
        <div className="mt-6 pt-6 border-t border-linie">
          <Link to="/pair" className={`${KNOPF_LEISE} flex items-center justify-center gap-2`}>
            <Monitor className="w-4 h-4" />
            Station koppeln
          </Link>
          <p className="text-xs text-tinte-3 text-center mt-2">
            Dieses Gerät als Station registrieren
          </p>
        </div>
      </Anmeldeflaeche>
    );
  }

  // REGISTRIERT: Chip und Admin nebeneinander, Chip voreingestellt
  return (
    <Anmeldeflaeche>
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-tinte">Anmeldung</h1>
        <p className="text-tinte-2 mt-1">Chip an den Leser halten oder als Admin anmelden</p>
      </div>

      {/* Umschalter Chip / Admin */}
      <div className="flex gap-2 mb-6">
        <button
          type="button"
          onClick={() => setMode('rfid')}
          className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors flex items-center justify-center gap-2 ${
            mode === 'rfid'
              ? 'border border-akzent bg-akzent text-akzent-satz'
              : 'border border-linie-2 bg-flaeche text-tinte-2 hover:text-tinte'
          }`}
        >
          <CreditCard className="w-4 h-4" />
          Chip
        </button>
        <button
          type="button"
          onClick={() => setMode('password')}
          className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors flex items-center justify-center gap-2 ${
            mode === 'password'
              ? 'border border-akzent bg-akzent text-akzent-satz'
              : 'border border-linie-2 bg-flaeche text-tinte-2 hover:text-tinte'
          }`}
        >
          <Lock className="w-4 h-4" />
          Admin
        </button>
      </div>

      {error && <Fehlerfeld text={error} />}

      {/* Chip-Anmeldung: der Leser tippt wie eine Tastatur in das
          unsichtbare Feld. Ablauf und Masse bleiben unveraendert. */}
      {mode === 'rfid' && (
        <div className="text-center py-8 cursor-pointer" onClick={handleCardClick}>
          {/* Verstecktes Feld fuer den Chip-Leser (Kiosk-Standard) */}
          <input
            ref={rfidInputRef}
            type="text"
            value={chipBuffer}
            onChange={handleRfidInputChange}
            onKeyDown={handleRfidKeyDown}
            className="sr-only"
            autoFocus
            aria-label="RFID Chip-ID"
            disabled={rfidState === 'success' || rfidState === 'error'}
          />

          {/* Wartet auf den Chip — der Ring pulsiert als Einladung */}
          {rfidState === 'ready' && (
            <>
              <div className="relative inline-block">
                <div className="absolute inset-0 rounded-full bg-akzent animate-ping" />
                <div className="relative w-20 h-20 rounded-full bg-akzent-tief flex items-center justify-center mx-auto">
                  <CreditCard className="w-10 h-10 text-akzent" />
                </div>
              </div>
              <p className="text-xl mt-6 font-medium text-tinte">Chip an Leser halten</p>
              <p className="text-tinte-2 mt-2">Wird automatisch erkannt</p>
            </>
          )}

          {/* Chip gelesen, wird geprueft */}
          {rfidState === 'scanning' && (
            <>
              <div className="w-20 h-20 rounded-full bg-akzent-tief flex items-center justify-center mx-auto">
                <Loader2 className="w-10 h-10 text-akzent animate-spin" />
              </div>
              <p className="text-xl mt-6 font-medium text-tinte">Wird geprüft...</p>
            </>
          )}

          {/* Erkannt — gleich geht es weiter */}
          {rfidState === 'success' && (
            <>
              <div className="w-20 h-20 rounded-full bg-flaeche-2 flex items-center justify-center mx-auto">
                <Check className="w-10 h-10 text-lauf" />
              </div>
              <p className="text-xl mt-6 font-medium text-lauf">Willkommen!</p>
            </>
          )}

          {/* Nicht erkannt — nach kurzer Zeit wieder bereit */}
          {rfidState === 'error' && (
            <>
              <div className="w-20 h-20 rounded-full bg-flaeche-2 flex items-center justify-center mx-auto">
                <XCircle className="w-10 h-10 text-stoer" />
              </div>
              <p className="text-xl mt-6 font-medium text-stoer">Chip nicht erkannt</p>
              <p className="text-tinte-2 mt-2">Bitte den Chip noch einmal an den Leser halten</p>
            </>
          )}
        </div>
      )}

      {/* Admin-Anmeldung mit E-Mail und Passwort */}
      {mode === 'password' && (
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-tinte-2 mb-1">
              E-Mail
            </label>
            <input
              id="email"
              type="email"
              ref={emailInputRef}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={EINGABE}
              required
              autoComplete="email"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-tinte-2 mb-1">
              Passwort
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={EINGABE}
              required
              autoComplete="current-password"
            />
          </div>

          <button type="submit" disabled={loading || !email || !password} className={KNOPF_PRIMAER}>
            {loading ? 'Wird geprüft...' : 'Anmelden'}
          </button>

          <div className="text-center mt-3">
            <Link
              to="/forgot-password"
              className="text-sm text-tinte-3 transition-colors hover:text-tinte-2"
            >
              Passwort vergessen?
            </Link>
          </div>
        </form>
      )}
    </Anmeldeflaeche>
  );
}
